import { forwardRef, useId } from "react";
import { cn } from "@/lib/utils";

const fieldBase =
  "w-full rounded-[10px] border border-neutral-200 bg-white px-3 text-sm text-ink placeholder:text-neutral-400 transition-colors focus:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-200 disabled:opacity-50 aria-[invalid=true]:border-red-400 aria-[invalid=true]:ring-red-100";

export const Input = forwardRef(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cn(fieldBase, "h-10", className)} {...props} />;
});

export const Textarea = forwardRef(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cn(fieldBase, "min-h-[96px] py-2.5 leading-relaxed", className)} {...props} />;
});

const CHEVRON =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23737373' stroke-width='2.2'><path d='M6 9l6 6 6-6'/></svg>\")";

export const Select = forwardRef(function Select({ className, children, style, ...props }, ref) {
  return (
    <select
      ref={ref}
      className={cn(fieldBase, "h-10 appearance-none bg-no-repeat pr-8", className)}
      style={{ backgroundImage: CHEVRON, backgroundSize: "12px", backgroundPosition: "right 12px center", ...style }}
      {...props}
    >
      {children}
    </select>
  );
});

export function Label({ className, ...props }) {
  return <label className={cn("text-[13px] font-medium text-neutral-700", className)} {...props} />;
}

/**
 * Label + control + hint/error. Pass a render function to get the generated id:
 * <Field label="Email" error={...}>{(id) => <Input id={id} />}</Field>
 */
export function Field({ label, hint, error, className, children, optional }) {
  const id = useId();
  const errId = `${id}-err`;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {optional && <span className="ml-1 font-normal text-neutral-400">(optional)</span>}
        </Label>
      )}
      {typeof children === "function" ? children(id, error ? errId : undefined) : children}
      {error ? (
        <span id={errId} role="alert" className="text-[12.5px] text-red-600">
          {error}
        </span>
      ) : hint ? (
        <span className="text-[12.5px] text-neutral-500">{hint}</span>
      ) : null}
    </div>
  );
}
